/**
 * K108-V (item C) — completion prompt for TICKET-launched training sessions.
 *
 * Problem: the "Did you complete this training?" modal was only triggered on the
 * Passport → Learning Center → foundational tab, so a session launched from a
 * ticket's Training tab (surface = ticket_sidebar) stayed "In progress" with no
 * way to attest. This finds the most recent PENDING session for this
 * technician+ticket that HAS monitored evidence and NO attestation yet — the
 * candidate the Training tab should prompt to complete.
 *
 * Read-only; degrades gracefully (no throw, returns null candidate). No DB change.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTrainingSessionMonitoringSummary } from '@/hooks/useTrainingSessionMonitoringSummary';
import {
  emptyMonitoringSummary,
  type TrainingSessionMonitoringSummary,
} from '@/lib/training/trainingSessionMonitoringSummary';

interface PendingTicketSession {
  id: string;
  technician_profile_id: string;
  artifact_id: string | null;
  metadata: Record<string, unknown> | null;
  completion_status: string | null;
  opened_at: string | null;
  artifact: {
    id: string;
    title: string | null;
    artifact_type: string | null;
    provider_name: string | null;
  } | null;
}

export interface TicketCompletionCandidate {
  /** Shaped for TrainingConsumptionConfirmDialog's `session` prop. */
  session: {
    id: string;
    technician_profile_id: string;
    artifact: { id: string; title: string; artifact_type: string; provider_name?: string };
  };
  summary: TrainingSessionMonitoringSummary;
  /** Terminal stop (tab closed / expired / navigated) → safe to auto-open once. */
  ended: boolean;
  /**
   * K108-Y: true when a PRIOR report exists but there is NEW monitored evidence
   * since it (the session was resumed) → prompt to UPDATE the report rather than
   * make a first attestation.
   */
  isUpdate: boolean;
}

/**
 * PURE candidate selection. Surfaces:
 *  - a session with monitored evidence and NO attestation yet (first report), OR
 *  - a session with a prior attestation AND newer monitored evidence since it (a
 *    resumed session that needs its report UPDATED).
 * Returns null when nothing needs attesting/updating.
 *
 * `opts.allowNoEvidence` (default false): when true, a PENDING session with NO
 * monitored evidence is ALSO surfaced (with an empty, evidence-unavailable
 * summary) so the user can still MANUALLY report completion — Companion evidence
 * is never required to report/complete. Auto-open stays evidence-gated separately
 * (see isAutoOpenEligible), so this only drives the manual "Record completion" CTA.
 */
export function selectTicketCompletionCandidate(
  sessions: PendingTicketSession[],
  summaries: Map<string, TrainingSessionMonitoringSummary> | undefined,
  opts?: { allowNoEvidence?: boolean },
): TicketCompletionCandidate | null {
  const allowNoEvidence = opts?.allowNoEvidence ?? false;
  for (const s of sessions) {
    let summary = summaries?.get(s.id);
    const noEvidence = !summary || summary.evidenceUnavailable;
    if (noEvidence && !allowNoEvidence) continue; // evidence-only mode → nothing to attest
    // No-evidence manual candidate: use an empty (evidenceUnavailable) summary so
    // the dialog shows the honest "no monitored evidence" line, never a fake %.
    if (!summary) summary = emptyMonitoringSummary(s.id);

    const att = (s.metadata ?? {})['trainingAttestation'] as Record<string, unknown> | undefined;
    let isUpdate = false;
    if (att) {
      // Already reported — only re-prompt when there is NEW evidence since the
      // report (i.e. the tech resumed and watched more). A no-evidence session
      // has no new evidence, so an already-reported one is correctly skipped.
      const attAt = typeof att.at === 'string' ? att.at : null;
      const last = summary.lastActivityAt;
      if (!(attAt && last && last > attAt)) continue;
      isUpdate = true;
    }
    return {
      session: {
        id: s.id,
        technician_profile_id: s.technician_profile_id,
        artifact: {
          id: s.artifact?.id ?? s.artifact_id ?? '',
          title: s.artifact?.title ?? 'Training',
          artifact_type: s.artifact?.artifact_type ?? 'long_video',
          provider_name: s.artifact?.provider_name ?? undefined,
        },
      },
      summary,
      ended: !!summary.stopped,
      isUpdate,
    };
  }
  return null;
}

export function useTicketTrainingCompletionPrompt(
  technicianProfileId: string | null | undefined,
  ticketId: string | null | undefined,
) {
  const sessionsQuery = useQuery<PendingTicketSession[]>({
    queryKey: ['ticket-training-completion-prompt', technicianProfileId, ticketId],
    enabled: !!technicianProfileId && !!ticketId,
    staleTime: 15 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(
          'id, technician_profile_id, artifact_id, metadata, completion_status, opened_at, artifact:training_artifacts(id, title, artifact_type, provider_name)',
        )
        .eq('technician_profile_id', technicianProfileId as string)
        .eq('ticket_id', ticketId as string)
        .eq('completion_status', 'pending')
        // CORE-AI DoD 11: expired sessions stop prompting; no judgment is made.
        .is('expired_at', null)
        .order('opened_at', { ascending: false })
        .limit(10);
      if (error) return [];
      return (data ?? []) as unknown as PendingTicketSession[];
    },
  });

  const sessions = useMemo(() => sessionsQuery.data ?? [], [sessionsQuery.data]);
  const sessionIds = useMemo(() => sessions.map((s) => s.id), [sessions]);
  const { data: summaries } = useTrainingSessionMonitoringSummary({
    sessionIds,
    enabled: sessionIds.length > 0,
  });

  const candidate = useMemo<TicketCompletionCandidate | null>(
    () => selectTicketCompletionCandidate(sessions, summaries),
    [sessions, summaries],
  );

  return { candidate, isLoading: sessionsQuery.isLoading };
}
